
import React from 'react';
import { cn } from '@/lib/utils';
import { Bot, MessageSquare, Zap, Sparkles, ShieldCheck, Lightbulb } from 'lucide-react';

interface AIReplyCardProps {
  text: string;
  style?: 'minimal' | 'modern' | 'classic' | 'colorful' | 'compact' | 'elegant';
  className?: string;
  animation?: string;
  isPositive?: boolean;
}

const AIReplyCard = ({ 
  text, 
  style = 'minimal',
  className,
  animation,
  isPositive = true
}: AIReplyCardProps) => {
  const cardStyles = {
    minimal: 'bg-blue-50 border border-blue-100 shadow-sm rounded-xl p-4',
    modern: 'glass-card bg-blue-50/50 p-4',
    classic: 'bg-sky-50 border-2 border-sky-100 rounded-lg p-4 shadow-md',
    colorful: 'bg-gradient-to-r from-indigo-50 to-blue-50 border border-blue-100 rounded-xl p-4 shadow-sm',
    compact: 'bg-gray-50 border-l-4 border-blue-500 border-y border-r shadow-sm rounded-r-lg p-3',
    elegant: 'bg-gradient-to-b from-blue-50 to-white border border-blue-100 rounded-xl p-5 shadow-md'
  };

  const getAIIcon = () => {
    switch (style) {
      case 'colorful':
        return <Sparkles size={18} className="text-indigo-500" />;
      case 'compact':
        return <Zap size={16} className="text-blue-600" />;
      case 'elegant':
        return <ShieldCheck size={18} className="text-blue-500" />;
      case 'classic':
        return <Lightbulb size={16} className="text-blue-600" />;
      default:
        return <Bot size={16} className="text-accent" />;
    }
  };

  // Handle header layouts based on style
  const renderHeader = () => {
    if (style === 'colorful') {
      return (
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center text-white">
            {getAIIcon()}
          </div>
          <p className="font-medium text-sm text-indigo-800">Réponse ReplyReply</p>
        </div>
      );
    } 
    
    if (style === 'compact') {
      return (
        <div className="flex items-center gap-1.5 mb-2">
          {getAIIcon()}
          <p className="font-semibold text-sm text-blue-800">ReviewGenie ReplyReply</p>
        </div>
      );
    }
    
    if (style === 'elegant') {
      return (
        <div className="flex flex-col items-center mb-3 text-center">
          <div className="w-12 h-12 mb-2 rounded-full bg-blue-100 flex items-center justify-center">
            {getAIIcon()}
          </div>
          <p className="font-medium text-md">Réponse ReplyReply</p>
          <div className="h-px w-16 bg-blue-200 my-2"></div>
        </div>
      );
    }
    
    // Default header for minimal, modern, classic
    return (
      <div className="flex items-center gap-2 mb-2">
        {getAIIcon()}
        <p className="font-medium text-sm text-accent">Réponse ReplyReply</p>
      </div>
    );
  };

  return (
    <div
      className={cn(
        cardStyles[style],
        animation,
        'transition-all duration-300',
        className
      )}
    >
      {renderHeader()}
      <p className={cn(
        "text-sm leading-relaxed text-balance",
        style === 'colorful' && 'text-indigo-900',
        style === 'compact' && 'text-blue-900',
        style === 'elegant' && 'text-center text-gray-700'
      )}>
        {text}
      </p>
    </div>
  );
};

export default AIReplyCard;
